import { isAxiosError } from "axios";

import { TextConfirmationModal } from "src/base-components/TextConfirmationModal";
import { useArchiveCaseTemplateVersion } from "src/cases/queries/templates";
import {
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel as CaseTemplateVersion,
  CaseTemplateVersionPublishState,
} from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";
import { getApiErrorDetail } from "src/utils/stringUtils";

type ArchiveTemplateVersionModalProps = {
  open: boolean;
  onClose: () => void;
  templateId: string;
  version: CaseTemplateVersion | null;
  onArchiveSuccess?: () => void;
};

export const ArchiveTemplateVersionModal: React.FC<
  ArchiveTemplateVersionModalProps
> = ({ open, onClose, templateId, version, onArchiveSuccess }) => {
  const { mutateAsync: archiveVersion } = useArchiveCaseTemplateVersion();

  const isPublishedVersion =
    version?.attributes.publish_state ===
    CaseTemplateVersionPublishState.PUBLISHED;

  const title = `Archive version ${version?.attributes.version_name || ""}`;
  const description = isPublishedVersion
    ? "Are you sure you want to archive this version? Archiving a published version may cause unexpected issues if there are open cases using this template. If you need to revert later, please contact the Taktile team for support."
    : "Are you sure you want to archive this version? If you need to revert later, please contact the Taktile team for support.";

  const handleArchive = async () => {
    if (!version) return;

    try {
      await archiveVersion({
        templateId,
        versionId: version.id,
        etag: version.attributes.etag,
      });

      onArchiveSuccess?.();
      onClose();
    } catch (error) {
      logger.error("Failed to archive version", error);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to archive version",
          description:
            "Someone made changes while you were editing. Please reload and try again",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to archive version",
          description: getApiErrorDetail(error, "An unknown error occurred"),
        });
        return;
      }

      toastActions.failure({
        title: "Failed to archive version",
        description: "An unknown error occurred",
      });
    }
  };

  return (
    <TextConfirmationModal
      cancelButtonText="Cancel"
      challengeText={version?.attributes.version_name ?? ""}
      confirmButtonText="Archive"
      description={description ?? ""}
      label="Please enter the version name to confirm"
      open={open}
      placeholder={version?.attributes.version_name ?? ""}
      title={title}
      variant="danger"
      onClose={onClose}
      onConfirm={handleArchive}
    />
  );
};
